import { AxiosError } from 'axios';
import DatePicker from 'components/base/DatePicker';
import React, { useState } from 'react';
import { Alert, Col, Form, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axiosInstance from 'utils/axiosInterceptors';
import { formatToUTCISO } from 'utils/formatToUTCISO';
import { toastConfig } from 'utils/toastConfig';
import * as yup from 'yup';

type TProofOfPayment = {
  BankName?: string;
  CheckNumber?: string;
  Amount: string;
  CheckDate?: string | Date;
  File: File | string;
  TransferredToAccountNumber?: string;
  PreparedByUserId?: number;
  PettyCashNumber?: string;
  PaymentReceivedBy?: string;
};

export type UploadPaymentProofResult = {
  isCheckNumberPossibleMismatch: boolean;
  enteredCheckNumber?: string;
  extractedCheckNumber?: string;
  message?: string;
};

type CreatePaymentProofFormProps = {
  branchDomain: string;
  supplierId: string;
  userId: number;
};

const CreatePaymentProofForm = React.forwardRef<
  {
    submitForm: () => Promise<
      true | UploadPaymentProofResult | false | undefined
    >;
  },
  CreatePaymentProofFormProps
>(({ supplierId, branchDomain, userId }, ref) => {
  const [paymentMethod, setPaymentMethod] = useState('check');
  const [proofOfPayment, setProofOfPayment] = useState<TProofOfPayment>({
    BankName: '',
    CheckNumber: '',
    Amount: '',
    CheckDate: new Date(),
    File: '',
    TransferredToAccountNumber: '',
    PreparedByUserId: userId,
    PettyCashNumber: '',
    PaymentReceivedBy: ''
  });
  const [mismatchResult, setMismatchResult] =
    useState<UploadPaymentProofResult | null>(null);

  const handleFormChange = <K extends keyof TProofOfPayment>(
    field: K,
    value: TProofOfPayment[K]
  ) => {
    setProofOfPayment(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files;
    if (file && file.length > 0) {
      handleFormChange('File', file[0]);
    }
  };

  const handleMethodChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const method = e.target.value;
    setPaymentMethod(method);
    setMismatchResult(null);

    setProofOfPayment(prev => ({
      ...prev,
      BankName:
        method === 'check' || method === 'bankTransfer' ? prev.BankName : '',
      CheckNumber: method === 'check' ? prev.CheckNumber : '',
      CheckDate: method === 'check' ? new Date() : '',
      PettyCashNumber: method === 'cash' ? prev.PettyCashNumber : '',
      PaymentReceivedBy: method === 'cash' ? prev.PaymentReceivedBy : '',
      TransferredToAccountNumber:
        method === 'bankTransfer' ? prev.TransferredToAccountNumber : '',
      File: prev.File,
      Amount: prev.Amount,
      PreparedByUserId: prev.PreparedByUserId
    }));
  };

  const schema = yup.object().shape({
    Amount: yup.number().required('Amount is required'),
    File: yup.mixed().required('Image is required'),
    CheckNumber: yup.string().when('$paymentMethod', {
      is: 'check',
      then: schema => schema.required('Check number is required'),
      otherwise: schema => schema.notRequired()
    }),
    BankName: yup.string().when('$paymentMethod', {
      is: (val: string) => val === 'check' || val === 'bankTransfer',
      then: schema => schema.required('Bank name is required'),
      otherwise: schema => schema.notRequired()
    }),
    TransferredToAccountNumber: yup.string().when('$paymentMethod', {
      is: 'bankTransfer',
      then: schema =>
        schema.required('Transferred to account number is required'),
      otherwise: schema => schema.notRequired()
    }),
    PettyCashNumber: yup.string().when('$paymentMethod', {
      is: 'cash',
      then: schema => schema.required('Petty cash number is required'),
      otherwise: schema => schema.notRequired()
    }),
    PaymentReceivedBy: yup.string().when('$paymentMethod', {
      is: 'cash',
      then: schema => schema.required('Payment received by is required'),
      otherwise: schema => schema.notRequired()
    })
  });

  const handleSubmit = async (): Promise<
    true | UploadPaymentProofResult | false | undefined
  > => {
    if (mismatchResult) {
      return mismatchResult;
    }

    try {
      await schema.validate(proofOfPayment, {
        abortEarly: false,
        context: { paymentMethod }
      });

      const formData = new FormData();

      if (proofOfPayment.File instanceof File) {
        formData.append('File', proofOfPayment.File);
      }
      formData.append('Amount', proofOfPayment.Amount.toString());
      formData.append(
        'PreparedByUserId',
        proofOfPayment.PreparedByUserId!.toString()
      );

      if (paymentMethod == 'check') {
        formData.append('BankName', proofOfPayment.BankName as string);
        formData.append('CheckNumber', proofOfPayment.CheckNumber!.toString());
        formData.append(
          'CheckDate',
          formatToUTCISO(proofOfPayment.CheckDate as string)
        );
      } else if (paymentMethod == 'cash') {
        formData.append(
          'PettyCashNumber',
          proofOfPayment.PettyCashNumber!.toString()
        );
        formData.append(
          'PaymentReceivedBy',
          proofOfPayment.PaymentReceivedBy!.toString()
        );
      } else if (paymentMethod == 'bankTransfer') {
        formData.append('BankName', proofOfPayment.BankName as string);
        formData.append(
          'TransferredToAccountNumber',
          proofOfPayment.TransferredToAccountNumber!.toString()
        );
      }

      const { data } = await toast.promise(
        axiosInstance.post<UploadPaymentProofResult>(
          `/api/v1/${branchDomain}/supplierPayments/${supplierId}/paymentProof`,
          formData,
          { timeout: 120000 }
        ),
        {
          pending: 'Submitting payment proof...',
          success: 'Payment proof submitted!',
          error: {
            render({ data }) {
              const error = data as AxiosError;
              return (
                (error.response?.data as { errorMessage?: string })
                  ?.errorMessage || error.message
              );
            }
          }
        }
      );

      if (data?.isCheckNumberPossibleMismatch) {
        setMismatchResult(data);
        toast.warning(
          data.message ||
            'Check number may not match the uploaded image. Please update or confirm.',
          { ...toastConfig, autoClose: 5000 }
        );
        return data;
      }

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        error.inner.forEach(err => {
          toast.error(err.message, { ...toastConfig, autoClose: 3000 });
        });
      } else if (error instanceof AxiosError) {
        toast.error(error.response?.data.errorMessage, {
          ...toastConfig,
          autoClose: 3000
        });
      }
      return false;
    }
  };

  React.useImperativeHandle(ref, () => ({
    submitForm: handleSubmit
  }));

  return (
    <>
      <Form className="w-100">
        {mismatchResult?.isCheckNumberPossibleMismatch && (
          <Alert variant="warning" className="mb-3">
            <div className="fw-bold mb-1">Possible check number mismatch</div>
            <div>
              Entered: <strong>{mismatchResult.enteredCheckNumber || '—'}</strong>
            </div>
            <div>
              Detected on image:{' '}
              <strong>{mismatchResult.extractedCheckNumber || 'Not found'}</strong>
            </div>
            <div className="mt-2 fs-9">
              Close this dialog, then update the check number or confirm it on
              the payment details page.
            </div>
          </Alert>
        )}
        <Row className="mb-3">
          <Form.Group as={Col}>
            <Form.Label>Payment Method</Form.Label>
            <Form.Select
              value={paymentMethod}
              onChange={handleMethodChange}
              disabled={!!mismatchResult}
            >
              <option value="check">Check</option>
              <option value="cash">Cash</option>
              <option value="bankTransfer">Bank Transfer</option>
            </Form.Select>
          </Form.Group>
        </Row>
        <Row className="mb-3">
          <Form.Group as={Col}>
            <Form.Label>Amount</Form.Label>
            <Form.Control
              type="text"
              value={proofOfPayment.Amount}
              onChange={e => handleFormChange('Amount', e.target.value)}
              disabled={!!mismatchResult}
            />
          </Form.Group>
        </Row>
        <Row className="mb-3">
          <Form.Group as={Col}>
            <Form.Label>Image</Form.Label>
            <Form.Control
              type="file"
              onChange={handleFileChange}
              disabled={!!mismatchResult}
            />
          </Form.Group>
        </Row>
        {(paymentMethod == 'check' || paymentMethod == 'bankTransfer') && (
          <Row className="mb-3">
            <Form.Group as={Col}>
              <Form.Label>Bank Name</Form.Label>
              <Form.Control
                type="text"
                value={proofOfPayment.BankName}
                onChange={e => handleFormChange('BankName', e.target.value)}
                disabled={!!mismatchResult}
              />
            </Form.Group>
          </Row>
        )}
        {paymentMethod == 'check' && (
          <>
            <Row className="mb-3">
              <Form.Group as={Col}>
                <Form.Label>Check Number</Form.Label>
                <Form.Control
                  type="text"
                  value={proofOfPayment.CheckNumber}
                  onChange={e =>
                    handleFormChange('CheckNumber', e.target.value)
                  }
                  disabled={!!mismatchResult}
                />
              </Form.Group>
            </Row>
            <Row className="mb-3">
              <Form.Group as={Col}>
                <Form.Label>Check Date</Form.Label>
                <DatePicker
                  value={proofOfPayment.CheckDate}
                  onChange={date => handleFormChange('CheckDate', date[0])}
                  options={mismatchResult ? { clickOpens: false } : undefined}
                />
              </Form.Group>
            </Row>
          </>
        )}
        {paymentMethod == 'cash' && (
          <>
            <Row className="mb-3">
              <Form.Group as={Col}>
                <Form.Label>Petty Cash Number</Form.Label>
                <Form.Control
                  type="text"
                  value={proofOfPayment.PettyCashNumber}
                  onChange={e =>
                    handleFormChange('PettyCashNumber', e.target.value)
                  }
                />
              </Form.Group>
            </Row>
            <Row className="mb-3">
              <Form.Group as={Col}>
                <Form.Label>Receive By</Form.Label>
                <Form.Control
                  type="text"
                  value={proofOfPayment.PaymentReceivedBy}
                  onChange={e =>
                    handleFormChange('PaymentReceivedBy', e.target.value)
                  }
                />
              </Form.Group>
            </Row>
          </>
        )}
        {paymentMethod == 'bankTransfer' && (
          <Row className="mb-3">
            <Form.Group as={Col}>
              <Form.Label>Transfer to Account Numbeer</Form.Label>
              <Form.Control
                type="text"
                value={proofOfPayment.TransferredToAccountNumber}
                onChange={e =>
                  handleFormChange('TransferredToAccountNumber', e.target.value)
                }
              />
            </Form.Group>
          </Row>
        )}
      </Form>
    </>
  );
});

export default CreatePaymentProofForm;
